import { AxiosError } from "axios";
import { transformLoanApplicationData } from "utils/transformLoanData";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { postRequest, useMutationWrapper } from "services/api/apiHelper";
import useGlobalStore from "app/store";
import { useNavigate } from "react-router-dom";
import { request } from "services/api/endpoints";
import { pages } from "utils/constants";
import { toast } from "react-hot-toast";
import useAuth from "./useAuth";
import { useTranslation } from "react-i18next";
import _ from "lodash";

export default function useLoanApplication() {
  const { t } = useTranslation();
  const [customization, loanCurrency, loanApplication, setLoanApplication, setLoanReferenceId] =
    useGlobalStore((state) => [
      state.customization,
      state.loanCurrency,
      state.loanApplication,
      state.setLoanApplication,
      state.setLoanReferenceId,
    ]);
  const [isDraft, setIsDraft] = useState(true);
  const navigate = useNavigate();
  const [authHeader] = useAuth();

  const methods = useForm({
    defaultValues: {
      ...loanApplication,
    },
  });

  function onLoanSubmit(data: any) {
    setLoanReferenceId(data.referenceId);
    if (isDraft) {
      toast.dismiss();
      toast.success(t("savedSuccessfully"), { position: "top-center" });
    }
    if (!isDraft) {
      navigate(pages.REVIEW);
    }
  }

  const onError = (error: AxiosError) => {
    toast.dismiss();
    if (error.response?.status === 401) {
      navigate(pages.HOME);
      toast.error(t("sessionExpired"), { duration: 5000 });
      return;
    }
    // @ts-ignore
    toast.error(error.response?.data?.message ?? t("somethingWentWrong"));
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { mutate: updateIndexedBorrower } = useMutationWrapper(postRequest);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { mutate: submitLoan, isLoading } = useMutationWrapper(postRequest, onLoanSubmit, onError);

  const submitForm = useCallback(
    async (data: any, draft: boolean) => {
      setLoanApplication({ ...loanApplication, ...data });
      setIsDraft(draft);
      // Show toast only when we are saving to draft
      if (draft) {
        toast.loading(t("savingApplication"), { position: "top-center" });
      }
      const { formData } = transformLoanApplicationData(
        {
          ...data,
          acquisitionPartnerProductId: customization?.productID,
        },
        customization.currency,
      );
      console.log("formData:::", formData);
      await submitLoan({
        url: request.LOAN_REQUESTS_ACQUISITION_PARTNER,
        data: {
          ..._.omit(formData, ["status"]),
          firstName: formData.fullName.split(" ")[0],
          surname: formData.fullName.split(" ")[1],
        },
        config: authHeader,
      });
    },
    [
      setLoanApplication,
      loanApplication,
      loanCurrency,
      updateIndexedBorrower,
      authHeader,
      submitLoan,
      t,
    ],
  );

  return [methods, submitForm, isLoading] as const;
}
